import {
  BookingStatusNames,
  bookingStatusType,
  IBookingStatus,
  IDiaryVenue,
  IBooking,
  IPouchRows,
  StatusBillWithCustomer,
  StatusClearTable,
  StatusOvertime,
  StatusSentSms,
  StatusSmsFailed,
  StatusStandbyPending,
  StatusWaitingAtBar,
  UnreliableBookingStatusIds,
} from 'shared-types/index';
import ObjectCopyService from 'external-services/object-copy-service/index';
import { cloneDeep, isEqual, orderBy } from 'lodash-es';
import SectionLookupService from 'external-services/section-lookup-service/sectionLookup.service';
import {
  ILoginActions,
  IPouchActions,
  IVenueActions,
  PouchDocType,
  VenueActionNames,
} from 'shared-types/ActionsTypes';
import PouchLoadService from 'external-services/pouch-load-service';
import { DiaryRootState } from 'appRoot/rootReducer';
import DateUtilsService from 'external-services/date-utils-service';
import { FontClr } from "external-services/pixijs-utils/types";

/**
 * Previous service name "VenueService"
 */
const NS = 'StatusConfigService';

interface IInitialProps {
  standByList?: boolean;
  trackEvent?: (eventName: any, values?: any) => void;
  LoginActions?: ILoginActions;
  VenueActions?: IVenueActions;
  PouchActions?: IPouchActions;
  statusChangedCallback: (
    fullStatusOptions: IBookingStatus[],
    statusOptions: IBookingStatus[]
  ) => void;
}

const CLR_UNCONFIRMED = '#FFE6A6';
const CLR_CONFIRMED = '#CEFFE1';
const CLR_FINISHED = '#DFDFDF';
const CLR_CANCELLED = '#ff0030';
const CLR_SEATED = '#0ae228';
const CLR_SEATED_ORDERED = '#267347';
const CLR_PARTIALLY_SEATED = '#f68b1f';
const CLR_STANDBY = '#4669ac';

class StatusConfigService {
  private standByList?: boolean;
  private trackEvent?: (eventName: any, values?: any) => void;
  private isFloorplanActive?: boolean;
  private venueTimezoneId: string;

  __forTests(): any {
    return {
      getCustomStatusOptions: this.getCustomStatusOptions,
    };
  }

  public init(
    props: IInitialProps
  ): Promise<{ statusOptions: IBookingStatus[]; fullStatusOptions: IBookingStatus[] }> {
    const {
      standByList = false,
      trackEvent = null,
      LoginActions,
      VenueActions,
      PouchActions,
      statusChangedCallback,
    } = props;
    this.standByList = standByList;
    this.trackEvent = trackEvent;
    this.reset();
    LoginActions.handleLogout(() => this.reset());
    VenueActions.handleVenueAction(VenueActionNames.VENUE_CHANGE, () => this.reset());
    PouchActions.handlePouchSyncedDoctype(PouchDocType.status, async (evt: any, payload: any) => {
      const { fullStatusOptions, statusOptions } = await this.getStatusOptions();
      statusChangedCallback(fullStatusOptions, statusOptions);
    });
    return this.addNewStatus(StatusSentSms)
      .then(() => this.addNewStatus(StatusSmsFailed))
      .then(() => this.addNewStatus(StatusStandbyPending))
      .then(() => this.getStatusOptions());
  }

  public changeVenueInfo(isFloorplanActive: boolean, venueTimezoneId: string) {
    this.isFloorplanActive = isFloorplanActive;
    this.venueTimezoneId = venueTimezoneId;
  }

  public reset(): void {
    SectionLookupService.setSectionLookUps(false);
  }

  public checkStatusOptions(
    name: string,
    types: string[],
    fullStatusOptions: IBookingStatus[]
  ): boolean {
    const customStatusOptions = this.getCustomStatusOptions(fullStatusOptions);
    const res = types.find((type) => {
      if (!customStatusOptions[type]) {
        return false;
      }
      return customStatusOptions[type].includes(name);
    });
    return !!res;
  }

  private getCustomStatusOptions(statusList: IBookingStatus[]): { [key: string]: string[] } {
    if (!statusList) {
      return {};
    }
    const statusNameObj: { [key: string]: string[] } = {};
    statusList.forEach((status) => {
      if (!statusNameObj[status.statusType]) {
        statusNameObj[status.statusType] = [status.name];
      } else {
        statusNameObj[status.statusType].push(status.name);
      }
    });
    return statusNameObj;
  }

  public updateUnconfirmedStatus(venue: IDiaryVenue): Promise<void> {
    return this.getStatusOptions().then((result) => {
      const unconfirmedStatus: IBookingStatus = result.statusOptions.find(
        (status) => status.name === BookingStatusNames.Unconfirmed
      );
      if (unconfirmedStatus) {
        // Update only if not already updated
        if (unconfirmedStatus.editable || !unconfirmedStatus.statusEditable) {
          unconfirmedStatus.editable = false;
          unconfirmedStatus.statusEditable = true;
          PouchLoadService.getDbLocal().put(unconfirmedStatus);
        }
      }

      return;
    });
  }

  /**
   * Saves unconfirmed as enabled, used with standby list
   */
  public forceUnconfirmedStatus(statusList?: IBookingStatus[]): Promise<void> {
    const fullStatusOptions = statusList; // || this.fullStatusOptions;
    const unconfirmedStatusOriginal: IBookingStatus = fullStatusOptions.find(
      (status) => status.name === BookingStatusNames.Unconfirmed
    );
    const originalCopy = ObjectCopyService.couchAndAngularSafeCopy(unconfirmedStatusOriginal);
    const unconfirmedStatus: IBookingStatus =
      this.disableUnconfirmedStatusForStandbyList(fullStatusOptions);
    const newCopy = ObjectCopyService.couchAndAngularSafeCopy(unconfirmedStatus);

    if (unconfirmedStatus && !isEqual(originalCopy, newCopy)) {
      return this.saveStatus(unconfirmedStatus)
        .then((success: any) =>
          console.log(
            NS,
            'Forced Unconfirmed status to enabled due to Standby List dependency',
            success
          )
        )
        .catch((err: any) => console.warn('Failed for force unconfirmed status to enabled'));
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Forces enabled for Unconfirmed status when standby list (SBL) is enabled because SBL is dependent on it.
   * Also toggles editability based on SBL.
   */
  private disableUnconfirmedStatusForStandbyList(
    statusList: IBookingStatus[] = null
  ): IBookingStatus {
    // const unconfirmedStatus: IBookingStatus = (statusList || this.fullStatusOptions).find(
    const unconfirmedStatus: IBookingStatus = statusList.find(
      (status) => status.name === BookingStatusNames.Unconfirmed
    );
    if (unconfirmedStatus) {
      if (this.standByList) {
        unconfirmedStatus.enabled = true;
        unconfirmedStatus.statusEditable = false;
      } else {
        unconfirmedStatus.statusEditable = true;
      }
    }

    return unconfirmedStatus;
  }

  private getStatusOptions(): Promise<{
    statusOptions: IBookingStatus[];
    fullStatusOptions: IBookingStatus[];
  }> {
    return this.getBookingStatus().then((statusList: IBookingStatus[]) => {
      this.disableUnconfirmedStatusForStandbyList(statusList);

      const fullStatusOptions: IBookingStatus[] = orderBy(statusList, ['order'], ['asc']);

      let statusOptions: IBookingStatus[] = statusList.filter((status: IBookingStatus) => {
        return status.enabled && status.hide !== true;
      });

      statusOptions = orderBy(statusOptions, ['order'], ['asc']);

      return {
        statusOptions,
        fullStatusOptions,
      };
    });
  }

  // moved getUnconfirmedStatusName to StatusService

  // moved getUnconfirmedStatusType to StatusService

  public getStatusByName(name: any, statusList: IBookingStatus[]): IBookingStatus {
    if (statusList) {
      return statusList.find(
        (status: IBookingStatus) => status.name.toLowerCase() === name.toLowerCase()
      );
    }

    return null;
  }

  /**
   * Returns a list of booking status options
   * @return {Array}
   */
  public getBookingStatus(): Promise<IBookingStatus[]> {
    const _this = this;
    return Promise.resolve(
      PouchLoadService.getDbLocal()
        .allDocs({
          include_docs: true,
          attachments: false,
          startkey: 'status_',
          endkey: 'status_\uffff',
        })
        .then((status: unknown) => {
          const _status: IPouchRows<IBookingStatus> = status as IPouchRows<IBookingStatus>;
          const statusList: IBookingStatus[] = _status.rows.map(
            (item: { id: string; key: string; value: { rev: string }; doc: IBookingStatus }) => {
              return item.doc;
            }
          );
          this.disableUnconfirmedStatusForStandbyList(statusList);
          if (_this.isFloorplanActive) {
            this.addFloorPlanStatusToList(statusList);
          } else {
            this.enableDisableFloorPlanStatus(statusList, false);
          }
          return statusList;
        })
    );
  }

  private addFloorPlanStatusToList(statusList: IBookingStatus[]) {
    const currentStatusIds = statusList.map((status, index) => status._id);
    // Add the floorplan status if they dont exist
    if (!currentStatusIds.includes(UnreliableBookingStatusIds.StatusWaitingAtBar)) {
      statusList.push({ ...StatusWaitingAtBar, order: statusList.length });
    }

    if (!currentStatusIds.includes(UnreliableBookingStatusIds.StatusClearTable)) {
      statusList.push({ ...StatusClearTable, order: statusList.length });
    }

    if (!currentStatusIds.includes(UnreliableBookingStatusIds.StatusOvertime)) {
      statusList.push({ ...StatusOvertime, order: statusList.length });
    }

    if (!currentStatusIds.includes(UnreliableBookingStatusIds.StatusBillWithCustomer)) {
      statusList.push({ ...StatusBillWithCustomer, order: statusList.length });
    }
  }

  // Disable floorplan status if floorplan is not active - so that they are removed while editing a booking
  private enableDisableFloorPlanStatus(statusList: IBookingStatus[], isEnabled: boolean) {
    return statusList.map((status) => {
      if (
        status._id === UnreliableBookingStatusIds.StatusWaitingAtBar ||
        status._id === UnreliableBookingStatusIds.StatusClearTable ||
        status._id === UnreliableBookingStatusIds.StatusOvertime ||
        status._id === UnreliableBookingStatusIds.StatusBillWithCustomer
      ) {
        status.statusEditable = isEnabled;
        status.enabled = isEnabled;
      }
      return status;
    });
  }

  public saveStatus(status: IBookingStatus): Promise<PouchDB.Core.Response> {
    let eventType = 'Edit status';
    if (!status._id) {
      eventType = 'New status';
      status._id = PouchLoadService.getNewId(['status']);
    }
    status.type = 'status';
    this.trackEvent(eventType, {
      id: status._id,
    });

    return Promise.resolve(PouchLoadService.getDbLocal().put(status));
  }

  public saveStatusList(statusList: IBookingStatus[]): Promise<void> {
    return Promise.resolve(PouchLoadService.getDbLocal().bulkDocs(statusList) as any);
  }

  /**
   * The legacy Dairy app in many cases needs to mutate the status objects, so this is just a convenience
   * function to return them cloned from the reducer.
   */
  public getMutableStatusOptionsFromStore(storeState: DiaryRootState): {
    statusOptions: IBookingStatus[];
    fullStatusOptions: IBookingStatus[];
  } {
    const fullStatusOptions = cloneDeep(storeState.statusReducer.fullStatusOptions);
    const statusOptions = cloneDeep(storeState.statusReducer.statusOptions);

    return { fullStatusOptions, statusOptions };
  }

  /**
   * Returns a list of booking status options
   *
   * @return {Array}
   */
  private addNewStatus(addStatus: IBookingStatus): Promise<void> {
    return this.getBookingStatus()
      .then((statusList: IBookingStatus[]) => {
        statusList = orderBy(statusList, ['order'], ['asc']);

        // adds new status if doesn't exist
        const hasStatus: boolean = statusList.some(
          (data: { _id: any }) => data._id === addStatus._id
        );
        if (!hasStatus) {
          statusList.push(addStatus);

          // refreshes the 'order' values for all
          statusList.forEach((o: IBookingStatus, i: number) => {
            o.order = i;
          });
        }

        return !hasStatus ? statusList : null;
      })
      .then((newStatusList: IBookingStatus[]) => {
        // adds type of 'status', which doesn't seem to exist by default and only gets added to newly saved statuses
        newStatusList &&
          newStatusList.forEach((o: IBookingStatus) => {
            if (!o.type) {
              o.type = 'status';
            }
          });
        return newStatusList;
      })
      .then((newStatusList: IBookingStatus[]) => {
        if (newStatusList?.length) {
          return PouchLoadService.getDbLocal().bulkDocs(newStatusList) as any;
        }
      });
  }

  public getStatusColorBasedOnStatusType(
    statusType: string,
    isSeated: boolean,
    isOrdered: boolean,
    isPaymentPending: boolean,
    customStatusColor: string
  ): { background: string; color?: string } {
    if (customStatusColor)
      return {
        background: customStatusColor,
        color: this.getCustomFontColor(customStatusColor),
      };
    if (statusType === bookingStatusType.unconfirmed) return { background: CLR_UNCONFIRMED };
    if (statusType === bookingStatusType.confirmed) return { background: CLR_CONFIRMED };
    if (statusType === bookingStatusType.finished) return { background: CLR_FINISHED };
    // prettier-ignore
    if (statusType === bookingStatusType.cancelled) return { background: CLR_CANCELLED, color: '#fff' };
    // prettier-ignore
    if (statusType === bookingStatusType.partiallySeated) return { background: CLR_PARTIALLY_SEATED, color: '#fff' };
    // prettier-ignore
    if (statusType === bookingStatusType.standbyPending) return { background: CLR_STANDBY, color: '#fff' };
    if (isSeated && !isOrdered) return { background: CLR_SEATED, color: '#fff' };
    if (isSeated && isOrdered) return { background: CLR_SEATED_ORDERED, color: '#fff' };
    if (isPaymentPending) return { background: CLR_UNCONFIRMED };

    return { background: '', color: '' };
  }

  public getFPStatusColorBasedOnStatusType(status: IBookingStatus): { background: string; color?: FontClr } {
    const { statusType, statusColor } = status
    if (statusColor) {
      return {
        background: statusColor,
        color: this.getCustomFontFPColor(statusColor),
      };
    }
    const colorMap: Record<string, {background: string, color: FontClr}> = {
      [bookingStatusType.unconfirmed]: {
        background: CLR_UNCONFIRMED,
        color: FontClr.black
      },
      [bookingStatusType.confirmed]: {
        background: CLR_CONFIRMED,
        color: FontClr.black
      },
      [bookingStatusType.finished]: {
        background: CLR_FINISHED,
        color: FontClr.black
      },
      [bookingStatusType.pendingPayment]: {
        background: CLR_UNCONFIRMED,
        color: FontClr.black
      },
      [bookingStatusType.cancelled]: {
        background: CLR_CANCELLED,
        color: FontClr.white
      },
      [bookingStatusType.partiallySeated]: {
        background: CLR_PARTIALLY_SEATED,
        color: FontClr.white
      },
      [bookingStatusType.standbyPending]: {
        background: CLR_STANDBY,
        color: FontClr.white
      },
      [bookingStatusType.seated]: {
        background: CLR_SEATED,
        color: FontClr.white
      },
    }

    if (colorMap[statusType]) {
      return colorMap[statusType]
    }
    console.warn('bookingStatusType color is not defined yet')
    return { background: '', color: FontClr.white };
  }

  public getCustomFontColor(backgroundColor: string): string {
    if (backgroundColor === CLR_UNCONFIRMED || backgroundColor === CLR_CONFIRMED) return '#000';
    return '#fff';
  }

  public getCustomFontFPColor(backgroundColor: string): FontClr {
    if ([CLR_UNCONFIRMED, CLR_CONFIRMED, CLR_FINISHED].includes(backgroundColor)) {
      return FontClr.black;
    }
    return FontClr.white;
  }

  public updatedBookingsBasedOnStatusChange = (
    bookings: IBooking[],
    statusOptions: IBookingStatus[]
  ): IBooking[] => {
    const timeNow = DateUtilsService.getVenueTime(this.venueTimezoneId);
    return bookings.map((booking) => {
      const isBookingDateExpired = DateUtilsService.checkIfBookingInPastDay(booking.time, timeNow);
      if (!isBookingDateExpired && booking.status.editable) {
        const selectedStatus = statusOptions.find((status) => status._id === booking.status._id);
        booking.status.name = selectedStatus.name;
        booking.status.code = selectedStatus.code;
        booking.status.statusColor = selectedStatus.statusColor;
      }
      return booking;
    });
  };
}

const instance = new StatusConfigService();
export default instance as StatusConfigService;
